import { API_TYPES } from "@src/appV2/api/constants";
import { z } from "zod";

import {
  ExpiryType,
  MAPPINGS_MANAGED_BY,
  REQUIREMENT_INCLUDE_RESOURCE,
  REQUIREMENT_RESOURCE,
  RequirementWorkflowType,
  WORKPLACE_VISIBILITY,
} from "./constants";

export const requirementIncludeSchema = z
  .array(z.nativeEnum(REQUIREMENT_INCLUDE_RESOURCE))
  .min(1)
  .optional();

export const checklistSchema = z.object({
  enabled: z.boolean(),
  description: z.string().optional(),
  items: z.array(z.string()).optional(),
});

export const helloSignConfigSchema = z.object({
  isEmbeddedFlowEnabled: z.boolean(),
  templateId: z.string(),
  fallbackStaticUrl: z.string(),
  instructions: z.string().nullish(),
});

export const quizConfigSchema = z.object({
  canDisplayAnswers: z.boolean(),
  maxMistakesAllowed: z.number().int().min(0),
});

export const pandaDocConfigSchema = z.object({
  templateId: z.string(),
  fallbackUrl: z.string().nullish(),
  instructions: z.string().nullish(),
  quizConfig: quizConfigSchema.optional(),
});

export const compositeConfigSchema = z.object({
  requirements: z.array(
    z.object({
      ctaText: z.string(),
      requirementId: z.string(),
    })
  ),
  instructions: z.string().nullish(),
});

export const requirementDataAttributesSchema = z.object({
  name: z.string().max(64, "Name is too long"),
  /*
    This field is deprecated
  */
  description: z.string().nullish(),
  instructions: z.string().optional(),
  expiryType: z.nativeEnum(ExpiryType),
  disableWorkerUpload: z.boolean().optional(),
  visibleToHCP: z.boolean().optional(),
  visibleToHCF: z.boolean().optional(),
  workplaceVisibility: z.nativeEnum(WORKPLACE_VISIBILITY).optional(),
  checklist: checklistSchema.nullish(),
  requirementWorkflowType: z.nativeEnum(RequirementWorkflowType),
  isAutomatedVerificationDecision: z.boolean().optional(),
  helloSignConfig: helloSignConfigSchema.nullish(),
  pandaDocConfig: pandaDocConfigSchema.nullish(),
  compositeConfig: compositeConfigSchema.nullish(),
  archived: z.boolean().optional(),
  requiredForBooking: z.boolean().optional(),
  validityDaysFromIssueDate: z.number().nullish(),
  mappingsManagedBy: z.nativeEnum(MAPPINGS_MANAGED_BY).nullish(),
});

export type RequirementDataAttributesDto = z.infer<typeof requirementDataAttributesSchema>;

const prerequisiteActionRelationshipSchema = z.object({
  data: z.array(
    z.object({
      type: z.literal(API_TYPES.prerequisiteAction),
      id: z.string(),
    })
  ),
});

export const requirementSchema = z.object({
  attributes: requirementDataAttributesSchema,
  id: z.string(),
  type: z.literal(REQUIREMENT_RESOURCE),
  relationships: z
    .object({
      prerequisiteActions: prerequisiteActionRelationshipSchema.optional(),
    })
    .optional(),
});

export type Requirement = z.infer<typeof requirementSchema>;

export const requirementResponseSchema = z.object({
  data: requirementSchema,
});

export type RequirementResponse = z.infer<typeof requirementResponseSchema>;

export const requirementListResponseSchema = z.object({
  data: z.array(requirementSchema),
  links: z.object({
    prev: z.string().optional(),
    prevCursor: z.string().optional(),
    next: z.string().optional(),
    nextCursor: z.string().optional(),
  }),
});

export type RequirementListResponse = z.infer<typeof requirementListResponseSchema>;
